<template>
  <div>
    <BFieldHint :label="$t('secrets.name')" hintKey="secrets.nameHelp">
      <b-input ref="nameInput" v-model="name" placeholder="MY_SECRET" required maxlength="100" pattern="[A-Z_][A-Z0-9_]*" :validation-message="$t('secrets.nameValidationError')"/>
    </BFieldHint>
    <BFieldHint :label="$t('secrets.value')" hintKey="secrets.valueHelp">
      <b-input ref="valueInput" v-model="value" type="textarea" :placeholder="$t('secrets.valuePlaceholder')" maxlength="100000" :rows="rows" required/>
    </BFieldHint>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, getCurrentInstance, computed, watch } from 'vue'
import { useStore } from 'vuex'
import BFieldHint from '@/components/BFieldHint.vue'

export default defineComponent({
  name: 'SecretForm',
  components: { BFieldHint },
  props: {
    rows: { type: Number, default: 8 }
  },
  emits: ['saved', 'valid'],
  setup(props, { emit, expose }) {
    const name = ref('')
    const value = ref('')
    const saving = ref(false)
    const vm = getCurrentInstance()
    const store = useStore()

    const nameInput = ref<any>(null)
    const valueInput = ref<any>(null)

    type NativeField = HTMLInputElement | HTMLTextAreaElement

    const getNative = (cmp: any): NativeField | null => {
    // Buefy uses different refs depending on type
    if (!cmp) return null
    return (
        (cmp.$refs && (cmp.$refs.input as NativeField)) ||
        (cmp.$refs && (cmp.$refs.textarea as NativeField)) ||
        // last-ditch fallback if internals change
        (cmp.$el?.querySelector?.('input, textarea') as NativeField | null) ||
        null
    )
    }

    const check = (cmp: any): boolean => {
      const el = getNative(cmp)
      return !!el && el.checkValidity()
    }

    const nameIsValid = computed(() => { void name.value; return check(nameInput.value) })
    const valueIsValid = computed(() => { void value.value; return check(valueInput.value) })
    const isValid = computed(() => nameIsValid.value && valueIsValid.value)
    watch(isValid, (v) => emit('valid', v), { immediate: true })

    type ToastProxy = { $buefy?: { toast?: { open?: (opts: { message: string; type: string }) => void } } }
    const openToast = (message: string, type: string) => {
      const proxy = vm?.proxy as unknown as ToastProxy
      proxy?.$buefy?.toast?.open?.({ message, type })
    }

    const save = async () => {
      saving.value = true
      try {
        await store.dispatch('upsertSecret', { name: name.value, value: value.value })
        openToast(vm?.proxy?.$t?.('secrets.toastSaved'), 'is-success')
        emit('saved', name.value)
      } catch (e) {
        openToast(vm?.proxy?.$t?.('secrets.toastSaveFailed'), 'is-danger')
      } finally {
        saving.value = false
      }
    }

    const submit = () => save()
    expose({ submit, isValid })
    return { name, value, saving, save, isValid, nameInput, valueInput }
  }
})
</script>


